import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, Link } from 'react-router-dom';
import { fetchDashboard as fetchDashboardAPI } from './utils/api';
import LandingPage from './components/LandingPage';
import './App.css';
import SchoolSelector from './components/SchoolSelector';
import Register from './components/Register';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Profile from './components/Profile';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import Staff from './components/Staff';
import Students from './components/Students';
import StudentDetail from './components/StudentDetail';
import Policies from './components/Policies';
import Agents from './components/Agents';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import Terms from './components/Terms';
import Classes from './components/Classes';
import LSS from './components/LSS';
import FeesAndDiscounts from './components/FeesAndDiscounts';
import Invoices from './components/Invoices';
import RecurringInvoices from './components/RecurringInvoices';
import RecurringInvoiceDetail from './components/RecurringInvoiceDetail';
import Expenses from './components/Expenses';
import Payments from './components/Payments';
import Reports from './components/Reports';
import ReportsStats from './components/ReportsStats';

import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Roster from './components/Roster';
import Chat from './components/Chat';

function App() {
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [timezone, setTimezone] = useState(localStorage.getItem('timezone'));
    const [loginData, setLoginData] = useState({ schools: [], invites: [], email: '', password: '' });
    const [dashboardData, setDashboardData] = useState(null);
    const [isChatOpen, setChatOpen] = useState(false);
    const [selectedChat, setSelectedChat] = useState(null);
    const [chatSize, setChatSize] = useState({width: 700, height: 500});

    const handleSchoolSelect = (selectedToken, timezone) => {
        setToken(selectedToken);
        setTimezone(timezone);
        setLoginData({ schools: [], invites: [], email: '', password: '' });
    };

    const handleLoginSuccess = (data, email, password) => {
        if (data.token) {
            setToken(data.token);
            setTimezone(data.timezone);
        } else {
            setLoginData({ schools: data.schools || [], invites: data.invites || [], email, password });
        }
    };

    useEffect(() => {
        if (token) {
            localStorage.setItem('token', token);
            localStorage.setItem('timezone', timezone);
        } else {
            localStorage.removeItem('token');
            localStorage.removeItem('timezone');
        }
    }, [token, timezone]);

    const fetchDashboard = async () => {
        if (token) {
            try {
                const response = await fetchDashboardAPI();
                setDashboardData(response.data);
            } catch (error) {
                console.error("Failed to fetch dashboard data", error);
            }
        }
    };

    useEffect(() => {
        fetchDashboard();
    }, [token]);

    const AuthRoutes = () => {
        if (token) {
            return <Navigate to="/dashboard" />;
        }

        if (loginData.schools.length > 0 || loginData.invites.length > 0) {
            return (
                <SchoolSelector
                    schools={loginData.schools}
                    invites={loginData.invites}
                    email={loginData.email}
                    password={loginData.password}
                    onSchoolSelect={handleSchoolSelect}
                />
            );
        }

        return (
            <Login
                setToken={setToken}
                setTimezone={setTimezone}
                onSchoolSelect={handleLoginSuccess}
            />
        );
    };

    const PrivateRoutes = () => {
        return token ? (
            <div className="app-container">
                {dashboardData && <TopBar user={dashboardData.user} setChatOpen={setChatOpen} />}
                <Sidebar />
                <div className="content">
                    <Outlet context={{ setSelectedChat, setChatOpen }} />
                </div>
                <Chat user={dashboardData ? dashboardData.user : null} roles={dashboardData ? dashboardData.roles : []} token={token} isChatOpen={isChatOpen} setChatOpen={setChatOpen} selectedChat={selectedChat} setSelectedChat={setSelectedChat} chatSize={chatSize} setChatSize={setChatSize} />
            </div>
        ) : (
            <Navigate to="/login" />
        );
    };

    return (
        <Router>
            <Routes>
                <Route path="/login" element={<AuthRoutes />} />
                <Route path="/register" element={token ? <Navigate to="/dashboard" /> : <Register setToken={setToken} setTimezone={setTimezone} />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />

                <Route element={<PrivateRoutes />}>
                    <Route path="/dashboard" element={<Dashboard dashboardData={dashboardData} fetchDashboard={fetchDashboard} />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/staff" element={<Staff />} />
                    <Route path="/students" element={<Students />} />
                    <Route path="/students/:studentId" element={<StudentDetail />} />
                    <Route path="/agents" element={<Agents />} />
                    <Route path="/invoices" element={<Invoices />} />
                    <Route path="/recurring-invoices" element={<RecurringInvoices />} />
                    <Route path="/recurring-invoices/:id" element={<RecurringInvoiceDetail />} />
                    <Route path="/expenses" element={<Expenses />} />
                    <Route path="/payments" element={<Payments />} />
                    <Route path="/fees-discounts" element={<FeesAndDiscounts />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/lss" element={<LSS />} />
                    <Route path="/classes" element={<Classes />} />
                    <Route path="/roster" element={<Roster />} />
                    <Route path="/policies" element={<Policies />} />
                    <Route path="/reports/financial" element={<Reports />} />
                    <Route path="/reports/stats" element={<ReportsStats />} />
                </Route>

                <Route path="/" element={<LandingPage />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            </Routes>
        </Router>
    );
}

export default App;
